/**
 * Talks to the ome API from the admin website. Sign-in gives an access token and a
 * refresh token (no cookies); they live in this site's localStorage. A 401 refreshes
 * once, then signs out.
 */

export const API_BASE = String(import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

const KEY = "foreach.admin.tokens";

export interface Tokens {
  access_token: string;
  refresh_token: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

let tokens: Tokens | null = readTokens();
const signedOutListeners: (() => void)[] = [];

function readTokens(): Tokens | null {
  try {
    const t = JSON.parse(localStorage.getItem(KEY) ?? "null") as Tokens | null;
    return t && typeof t.access_token === "string" && typeof t.refresh_token === "string" ? t : null;
  } catch {
    return null;
  }
}

export function saveTokens(t: Tokens | null) {
  tokens = t;
  try {
    if (t) localStorage.setItem(KEY, JSON.stringify({ access_token: t.access_token, refresh_token: t.refresh_token }));
    else localStorage.removeItem(KEY);
  } catch {
    // Private windows can refuse storage; the session then lasts until the tab closes.
  }
}

export const isSignedIn = () => tokens !== null;
export const onSignedOut = (f: () => void) => signedOutListeners.push(f);

export function url(path: string): string {
  return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

async function errorOf(r: Response): Promise<ApiError> {
  const j = (await r.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
  return new ApiError(r.status, j.error?.code ?? "error", j.error?.message ?? `The server answered ${r.status}.`);
}

/** A call that needs no sign-in (email code, link redemption, refresh). */
export async function publicPost<T>(path: string, body: unknown): Promise<T> {
  let r: Response;
  try {
    r = await fetch(url(path), { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  } catch {
    throw new ApiError(0, "offline", "Can't reach ForEach. Check your connection.");
  }
  if (!r.ok) throw await errorOf(r);
  return (await r.json()) as T;
}

export function signOut() {
  const t = tokens;
  saveTokens(null);
  if (t) void publicPost("/v1/auth/logout", { refresh_token: t.refresh_token }).catch(() => {});
  for (const f of signedOutListeners) f();
}

/** A signed-in call. Returns the raw response so callers can read JSON or a file. */
export async function call(method: string, path: string, body?: unknown): Promise<Response> {
  for (let attempt = 0; attempt < 2; attempt++) {
    if (!tokens) break;
    const headers: Record<string, string> = { authorization: `Bearer ${tokens.access_token}` };
    if (body !== undefined) headers["content-type"] = "application/json";
    let r: Response;
    try {
      r = await fetch(url(path), { method, headers, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
    } catch {
      throw new ApiError(0, "offline", "Can't reach ForEach. Check your connection.");
    }
    if (r.status !== 401) {
      if (!r.ok) throw await errorOf(r);
      return r;
    }
    if (attempt === 0) {
      try {
        saveTokens(await publicPost<Tokens>("/v1/auth/refresh", { refresh_token: tokens.refresh_token }));
        continue;
      } catch {
        break;
      }
    }
  }
  signOut();
  throw new ApiError(401, "unauthorized", "Your sign-in ended. Sign in again.");
}

export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await call(method, path, body);
  if (r.status === 204) return {} as T;
  return (await r.json()) as T;
}

export const get = <T>(path: string) => api<T>("GET", path);

/** Uploads a file to /v1/documents (raw body). Returns its document id. */
export async function uploadDocument(file: File): Promise<{ id: string; name: string }> {
  if (!tokens) throw new ApiError(401, "unauthorized", "Sign in first.");
  const send = (t: Tokens) =>
    fetch(url("/v1/documents"), {
      method: "POST",
      headers: { authorization: `Bearer ${t.access_token}`, "content-type": file.type || "text/plain", "x-file-name": encodeURIComponent(file.name) },
      body: file,
    });
  let r: Response;
  try {
    r = await send(tokens);
    if (r.status === 401) {
      saveTokens(await publicPost<Tokens>("/v1/auth/refresh", { refresh_token: tokens.refresh_token }));
      r = await send(tokens!);
    }
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError(0, "offline", "Can't reach ForEach. Check your connection.");
  }
  if (!r.ok) throw await errorOf(r);
  return (await r.json()) as { id: string; name: string };
}

/** Redeems a one-time link from the app (`#code=…&verifier=…&ws=…`). Returns the workspace id it named. */
export async function redeemLink(hash: string): Promise<{ redeemed: boolean; workspaceId: string | null }> {
  const h = new URLSearchParams(hash.replace(/^#/, ""));
  const code = h.get("code");
  const verifier = h.get("verifier");
  if (!code || !verifier) return { redeemed: false, workspaceId: null };
  saveTokens(await publicPost<Tokens>("/v1/auth/token", { code, code_verifier: verifier }));
  return { redeemed: true, workspaceId: h.get("ws") };
}
